import { css } from "@emotion/react";
import {
  colors,
  timings,
  breakpoints,
} from "@mytutor/shared-react-web-components/lib/theme";
import { PrimaryCTAButton } from "@mytutor/shared-react-web-components/lib/PrimaryCTA";
import React, { useState, useRef, useEffect } from "react";
import { useMultiSelectFilterState } from "../../hooks/useMultiSelectFilter";

const categoryFilterStyle = css`
  width: 100%;
  position: relative;
  .dropdownContainer {
    background-color: ${colors.neutral1};
    box-shadow: 0 6px 6px hsla(0, 0%, 50%, 0.5);
    max-width: 100%;
    padding: 1em;
    width: 100vw;
    height: 100dvh;
    top: 0;
    left: 0;
    position: fixed;
    z-index: 2147483002;
    display: grid;
    grid-template-rows: 40px minmax(0, 1fr) 66px;
    align-items: flex-start;

    @media (min-width: ${breakpoints.tablet}px) {
      position: absolute;
      width: 200%;
      height: auto;
      max-width: 40vw;
      top: 100%;
      left: 1px;
      display: block;
    }

    @media (min-width: ${breakpoints.desktop}px) {
      width: 100%;
    }
  }
  .dropdownContainerHidden {
    display: none;
  }

  .dropdownContainer {
     padding-top: 1em; 
    @media (min-width: 481px) and (max-width: 767px) {
      padding-top: 5em;
    }
  }
  .mobileHeading {
    display: grid;
    grid-template-columns: 60px 1fr 60px;
    justify-content: center;
    align-items: center;
    font-size: 18px;
    font-weight: 700;

    @media (min-width: ${breakpoints.tablet}px) {
      display: none;
    }

    & h2 {
      grid-column: 2;
      text-align: center;
      margin: 0;
      font-size: 22px;
    }

    & .categoryFilterClose {
      grid-column: 3;
      justify-self: flex-end;
      position: static;
      margin: 0;
    }
  }

  button {
    display: inline-block;
    font-size: 1.125rem;
    width: 100%;
    height: auto;
    line-height: 1.2;
    padding: 1em 1em;

    @media (min-width: ${breakpoints.tablet}px) {
      width: fit-content;
      padding: 0.75em 1em;
      font-size: 1em;
      position: static;
      transform: none;
    }
  }
  .categoryButton {
    border: 1px solid ${colors.neutral4};
    background-color: ${colors.neutral1};
    text-align: left;
    font-size: 14px;
    border-radius: 4px;
    height: 48px;
    padding: 12px 16px 12px 8px;
    display: grid;
    grid-template-columns: 1fr 20px;
    align-items: center;
    justify-content: flex-start;
    overflow: hidden;
    width: 100%;
    transition: ${timings.medium};
  }
  .categoryButton:hover {
    border: 1px ${colors.primary6} solid;
  }
  .categoryButtonText {
    color: #333333;
    font-weight: 600;
  }

  .categoryButtonTextSelected {
    height: fit-content;
    display: flex;
    justify-content: flex-start;
    flex-wrap: nowrap;
    overflow: hidden;
    width: 100%;
    font-family: "FoundersGrotesk", sans-serif;
    font-weight: 500;
    font-size: 16px;
  }
  .categoryRangeSelected {
    font-family: "FoundersGrotesk", sans-serif;
    font-size: 16px;
    font-weight: 500;
    margin: 0;
    margin-right: 0.5em;
    background-color: ${colors.bgcolor1};
    padding: 0 2px;
    height: 1.2em;
    line-height: 1.2em;
    width: fit-content;
    min-width: 72px;
  }

  .dropdownFooter {
    display: flex;
    justify-items: flex-start;
    align-items: center;

    .resetLink {
      display: inline-block;
      font-family: "FoundersGrotesk", sans-serif;
      margin: 0 0 0 1rem;
      padding: 0.25em;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      color: #03776e;
      border: none;
      background: none;
      outline: 0;
    }
  }
`;

const dropdownStyle = css`
  height: 100%;
  overflow: auto;
  .checkboxAndLabel {
    display: flex;
    align-items: center;
  }
  label {
    font-size: 1rem;
    font-weight: 400;
    margin: 0px;
    margin-left: 8px;
  }
  p {
    padding-left: 44px;
    margin: -3px 0 10px 0;
    color: ${colors.neutral5};
    line-height: 1.25em;
    padding-bottom: 1em;
  }

  input[type="checkbox"] {
    transition: all 0.15s cubic-bezier(0, 1.05, 0.72, 1.07);
    border-radius: 0.25em;
    border: 1px solid ${colors.neutral11};
    width: 1.8em;
    height: 1.8em;
    color: ${colors.neutral1};
  }
  input[type="checkbox"]:checked,
  input[type="checkbox"]:checked:hover {
    accent-color: ${colors.primary5};
    color: ${colors.neutral1};
  }
  input[type="checkbox"]:hover {
    border-color: ${colors.primary4};
  }
  input[type="checkbox"]:focus {
  }

  input[type="checkbox"]:checked + label {
    font-weight: 500;
  }
`;

const categoriesArray = [
  {
    name: "SEN trained tutors",
    description:
      "Tutors who are trained to support students with Special Educational Needs.",
    value: "sen",
  },
];

const categoryOptions = ["sen", "" ];

const TutorCategoryFilter = ({ urlParameters }) => {
  const [isOpen, setIsOpen] = useState(false);

  const args = {
    filterName: "category",
    initVal: urlParameters.category,
    selectAllInit: {},
    allowedOptions: categoryOptions,
    isOpen: isOpen,
    setIsOpen: setIsOpen,
  };

  const {
    currentSelection,
    setCurrentSelection,
    appliedSelection,
    handleChange,
    handleToggle,
    handleSubmit,
    handleReset,
  } = useMultiSelectFilterState(args);

  const getSelectedCategories = () => {
    const selectedCategories = categoriesArray.filter(
      (category) =>
        urlParameters.category && urlParameters.category.includes(category.value)
    );

    if (!selectedCategories.length) {
      return <span>All tutors</span>;
    }

    const selectedCatNames = selectedCategories.map((p) => p.name);
    return <p className="categorySelected">{selectedCatNames.join(", ")}</p>;
  };

  const refComponentContainer = useRef(null);
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        isOpen &&
        refComponentContainer.current &&
        !refComponentContainer.current.contains(event.target)
      ) {
        setCurrentSelection(appliedSelection);
        setIsOpen(false);
      }
    }

    // Bind the event listener
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [appliedSelection, isOpen, setCurrentSelection]);

  return (

      <div
        className="categoryWrapper"
        css={categoryFilterStyle}
        ref={refComponentContainer}
      >
        <button className="categoryButton" onClick={handleToggle}>
          <div className="categoryButtonText categoryButtonTextSelected">
            {getSelectedCategories()}
          </div>

          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/view-tutors/chevron-vertical-primary7.svg"
            alt="An openning icon"
          />
        </button>
        <div
          className={isOpen ? "dropdownContainer" : "dropdownContainerHidden"}
        >
          <div className="mobileHeading">
            {/* dynamically insert clear link here */}
            <h2>Tutoring categories</h2>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <button
              onClick={handleToggle}
              className="categoryFilterClose"
              aria-label="Close category filter"
              style={{
                background: `url('/view-tutors/icon.svg') no-repeat center`,
                backgroundSize: 'contain',
                border: 'none',
                width: '13px',
                height: '13px',
                cursor: 'pointer',
                padding: 0,
              }}
            />
          </div>
          <div css={dropdownStyle}>
            {categoriesArray.map((category) => (
              <React.Fragment key={category.value}>
                <div className="checkboxAndLabel">
                  <input
                    type="checkbox"
                    id={`checkboxCategory-${category.value}`}
                    name="category"
                    value={category.value}
                    checked={currentSelection[category.value]}
                    onChange={(e) =>
                      handleChange({
                        value: category.value,
                        checkboxState: e.currentTarget.checked,
                      })
                    }
                  />
                  <label> {category.name}</label>
                </div>
                <p>{category.description}</p>
              </React.Fragment>
            ))}
          </div>
          <footer className="dropdownFooter">
            <PrimaryCTAButton
              className="applyButton"
              onClick={() => handleSubmit("")}
            >
              Apply filters
            </PrimaryCTAButton>
            <button className="resetLink" onClick={handleReset}>
              Clear filters
            </button>
          </footer>
        </div>
      </div>
  );
};
export default TutorCategoryFilter;
